import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, forkJoin, map, Observable, of, switchMap } from 'rxjs';
import { ApiService, API_ENDPOINTS, CATEGORY } from '@core';
import { ApiResponse } from '@core/models/api.models';
import {
  AssignFloorRequest,
  AssignFloorResponse,
  DetectionScopeResponse,
  DetectionScopeSaveRequest,
  DetectionScopeSaveResponse,
  CreateProjectRequest,
  DeleteDocumentRequest,
  DeleteDocumentResponse,
  GetAssignedFloorsResponse,
  MainDocument,
  PointsDataResponse,
  UpdateContourPointsResponse,
  PricingDrawerResponse,
  PricingDrawerSaveRequest,
  PricingDrawerSaveResponse,
  PricingSummaryResponse,
  ProjectListData,
  ProjectListParams,
  ProjectMutationResponse,
  ScalesResponse,
  SplitPagesRequest,
  SplitPagesResponse,
  StepperSummaryResponse,
  UpdateProjectRequest,
  ViewDocumentsData,
} from '@core/models/project.models';

@Injectable({ providedIn: 'root' })
export class ProjectService {
  private readonly api = inject(ApiService);
  private readonly http = inject(HttpClient);

  getProjects(params: ProjectListParams = {}): Observable<ApiResponse<ProjectListData>> {
    return this.api.get<ProjectListData>(API_ENDPOINTS.project.list, {
      name:  params.name  ?? '',
      page:  params.page  ?? 1,
      limit: params.limit ?? 25,
    });
  }

  createProject(payload: CreateProjectRequest): Observable<ApiResponse<ProjectMutationResponse>> {
    return this.api.post<ProjectMutationResponse>(API_ENDPOINTS.project.create, payload);
  }

  updateProject(projectId: number, payload: UpdateProjectRequest): Observable<ApiResponse<ProjectMutationResponse>> {
    return this.api.put<ProjectMutationResponse>(API_ENDPOINTS.project.update(projectId), payload);
  }

  deleteProject(projectId: number): Observable<ApiResponse<unknown[]>> {
    return this.api.delete<unknown[]>(API_ENDPOINTS.project.delete(projectId));
  }

  deleteDocument(payload: DeleteDocumentRequest): Observable<ApiResponse<DeleteDocumentResponse>> {
    return this.api.post<DeleteDocumentResponse>(API_ENDPOINTS.project.deleteDocuments, payload);
  }

  uploadDocuments(projectId: number, file: File): Observable<ApiResponse<unknown>> {
    const form = new FormData();
    form.append('project_id', String(projectId));
    form.append('doc_type', 'main');
    form.append('files', file);
    return this.api.post<unknown>(API_ENDPOINTS.project.uploadDocuments, form);
  }

  replaceMainDocument(projectId: number, file: File): Observable<ApiResponse<unknown>> {
    return this.getProjectDocuments(projectId).pipe(
      map((response) => response.data.documents.main),
      switchMap((documents) => this.deleteMainDocuments(documents)),
      switchMap(() => this.uploadDocuments(projectId, file)),
    );
  }

  getProjectDocuments(projectId: number): Observable<ApiResponse<ViewDocumentsData>> {
    return this.api.get<ViewDocumentsData>(API_ENDPOINTS.project.viewDocuments, { project_id: projectId });
  }

  getDetectionScope(projectId: number): Observable<ApiResponse<DetectionScopeResponse>> {
    return this.api.get<DetectionScopeResponse>(API_ENDPOINTS.project.detectionScope(projectId));
  }

  saveDetectionScope(
    projectId: number,
    payload: DetectionScopeSaveRequest,
  ): Observable<ApiResponse<DetectionScopeSaveResponse>> {
    return this.api.post<DetectionScopeSaveResponse>(API_ENDPOINTS.project.detectionScope(projectId), payload);
  }

  getScales(): Observable<ApiResponse<ScalesResponse>> {
    return this.api.get<ScalesResponse>(API_ENDPOINTS.project.scales);
  }

  addScale(projectId: number, scale: string, fileTypeId: number): Observable<ApiResponse<unknown>> {
    return this.api.post<unknown>(API_ENDPOINTS.onboarding.addScale, {
      project_id: projectId,
      scale,
      file_type_id: fileTypeId,
    });
  }

  assignFloor(payload: AssignFloorRequest): Observable<ApiResponse<AssignFloorResponse>> {
    return this.api.post<AssignFloorResponse>(API_ENDPOINTS.project.assignFloor, payload);
  }

  getAssignedFloors(projectId: number): Observable<ApiResponse<GetAssignedFloorsResponse>> {
    return this.api.get<GetAssignedFloorsResponse>(API_ENDPOINTS.project.getAssignedFloors(projectId));
  }

  deleteAssignedFloor(id: number): Observable<ApiResponse<unknown>> {
    return this.api.delete<unknown>(API_ENDPOINTS.project.deleteAssignedFloor(id));
  }

  editAssignedFloor(id: number, assignedFloorName: string): Observable<ApiResponse<unknown>> {
    return this.api.put<unknown>(API_ENDPOINTS.project.editAssignedFloor(id), { assigned_floor_name: assignedFloorName });
  }

  splitPages(projectId: number): Observable<ApiResponse<SplitPagesResponse>> {
    const payload: SplitPagesRequest = { project_id: projectId };
    return this.api.post<SplitPagesResponse>(API_ENDPOINTS.pdfCropper.splitPages, payload);
  }

  getPointsData(projectId: number, fileTypeId: number): Observable<ApiResponse<PointsDataResponse>> {
    const endpoint = fileTypeId === CATEGORY.FIXTURES.numericId
      ? API_ENDPOINTS.project.getPlumbingPointsData(projectId)
      : fileTypeId === CATEGORY.HVAC.numericId
      ? API_ENDPOINTS.project.getHvacPointsData(projectId)
      : fileTypeId === CATEGORY.COLUMNS.numericId
      ? API_ENDPOINTS.project.getColumnsPointsData(projectId)
      : fileTypeId === CATEGORY.STEEL_BEAMS.numericId
      ? API_ENDPOINTS.project.getBeamsPointsData(projectId)
      : API_ENDPOINTS.project.getPointsData(projectId);
    const request = this.api.get<PointsDataResponse>(endpoint, { file_type_id: fileTypeId });
    if (fileTypeId !== CATEGORY.COLUMNS.numericId) return request;

    return request.pipe(
      switchMap((response) => this.hasColumnsData(response.data) ? of(response) : this.demoColumnsPointsData()),
      catchError(() => this.demoColumnsPointsData()),
    );
  }

  private demoColumnsPointsData(): Observable<ApiResponse<PointsDataResponse>> {
    return this.http.get<ApiResponse<PointsDataResponse>>('/assets/demo/columns-result.json').pipe(
      map((response) => ({
        ...response,
        success: true,
        message: response.message || 'Loaded demo columns result.',
      })),
    );
  }

  private hasColumnsData(data: PointsDataResponse | null | undefined): boolean {
    const contourCount = data?.contour_points?.some((entry) =>
      (((entry.points_data as any)?.models?.columns?.contours ?? []) as unknown[]).length > 0,
    );
    const measurementCount = (((data as any)?.all_measurements ?? []) as unknown[]).length > 0;
    return !!contourCount || measurementCount;
  }

  updateContourPoints(payload: {
    floor_name: string;
    floor_number: number;
    project_id: number;
    scale: string;
    id: number;
    file_type_id?: number;
    points_data: { contours: unknown[] };
  }): Observable<ApiResponse<UpdateContourPointsResponse>> {
    return this.api.post<UpdateContourPointsResponse>(API_ENDPOINTS.project.updateContourPoints, payload);
  }

  updateContourPointsHvac(payload: {
    floor_name: string;
    floor_number: number;
    project_id: number;
    scale: string;
    id: number;
    file_type_id?: number;
    points_data: { contours: unknown[] };
  }): Observable<ApiResponse<UpdateContourPointsResponse>> {
    return this.api.post<UpdateContourPointsResponse>(API_ENDPOINTS.project.updateContourPointsHvac, payload);
  }

  savePricingDrawer(
    projectId: number,
    payload: PricingDrawerSaveRequest,
  ): Observable<ApiResponse<PricingDrawerSaveResponse>> {
    return this.api.post<PricingDrawerSaveResponse>(API_ENDPOINTS.project.pricingDrawer(projectId), payload);
  }

  savePlumbingPricingDrawer(
    projectId: number,
    payload: PricingDrawerSaveRequest,
  ): Observable<ApiResponse<PricingDrawerSaveResponse>> {
    return this.api.post<PricingDrawerSaveResponse>(API_ENDPOINTS.project.pricingDrawerPlumbing(projectId), payload);
  }

  getPricingDrawer(
    projectId: number,
    categoryId: number,
    floorName: string,
  ): Observable<ApiResponse<PricingDrawerResponse>> {
    return this.api.get<PricingDrawerResponse>(API_ENDPOINTS.project.pricingDrawer(projectId), {
      category_id: categoryId,
      floor_name: floorName,
    });
  }

  getPlumbingPricingDrawer(
    projectId: number,
    categoryId: number,
    floorName: string,
  ): Observable<ApiResponse<PricingDrawerResponse>> {
    return this.api.get<PricingDrawerResponse>(API_ENDPOINTS.project.pricingDrawerPlumbing(projectId), {
      category_id: categoryId,
      floor_name: floorName,
    });
  }

  getPricingSummary(projectId: number): Observable<ApiResponse<PricingSummaryResponse>> {
    return this.api.get<PricingSummaryResponse>(API_ENDPOINTS.project.pricingSummary(projectId));
  }

  getStepperSummary(projectId: number): Observable<ApiResponse<StepperSummaryResponse>> {
    return this.api.get<StepperSummaryResponse>(API_ENDPOINTS.project.stepperSummary(projectId));
  }

  private deleteMainDocuments(documents: MainDocument[]): Observable<unknown> {
    if (!documents.length) {
      return of(null);
    }

    return forkJoin(
      documents.map((document) =>
        this.deleteDocument({
          doc_id: document.doc_id,
          doc_type: 'main',
        }),
      ),
    );
  }
}
